import React from 'react';
import { Field } from 'react-final-form';
import {
  Button, Divider, FormControl, FormHelperText, Input, useDisclosure, useToast,
} from '@chakra-ui/react';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { useHistory, useParams } from 'react-router-dom';
import { ValidationErrors } from 'final-form';
import { NIL } from 'uuid';
import {
  FaFilePdf, FaLock, FaBan, FaFolderOpen,
} from 'react-icons/fa';
import { FiEye } from 'react-icons/fi';
import useCurrentUser from '../../hooks/useCurrentUser';
import MarkdownTextInput from './MarkdownTextInput';
import ToggleInput from './ToggleInput';
import DateInputControl from './DateInput';
import TextInputControl from './TextInput';
import TrackingUrlField from './TrackingUrlField'; // item 1310
import CurrencyInputControl from './CurrencyInput';
import SelectInputControl from './SelectInput';
import FormRow from './FormRow';
import { US_STATES } from '../../models/usStates';
import ResponsiveStack from '../ResponsiveStack';
import DocumentFormScaffold from './DocumentFormScaffold';
import FieldLabel from './FieldLabel';
import ServiceAddressSection from './ServiceAddressSection';
import ReceivedPoAttachments from '../ReceivedPoAttachments';
import RnwyEnrollPanel from './RnwyEnrollPanel';
import SendDocumentButton from './SendDocumentButton';
import IconInvoice from '../../assets/Icon-Invoice.svg';
import IconInvoiceWhite from '../../assets/Icon-Invoice-White.svg';
import { Invoice, InvoiceItem, InvoiceStatus } from '../../models/invoice';
import useInvoice from '../../hooks/useInvoice';
import FinalizeInvoiceModal from '../FinalizeInvoiceModal';
import DocumentStatusStamp from '../DocumentStatusStamp';
import InvoiceReminderChip from '../InvoiceReminderChip'; // item 1163e
import CommissionAdjustModal from '../CommissionAdjustModal';
import InvoiceItemsFormElement from '../InvoiceItemsFormElement';
import InvoicePdfOutputPanel from '../InvoicePdfOutputPanel'; // item 1488
import useProposals from '../../hooks/useProposals';
import { Proposal } from '../../models/proposal';
import useWorkOrders from '../../hooks/useWorkOrders';
import { WorkOrder } from '../../models/workOrder';
import useContacts from '../../hooks/useContacts';
import { ContactDto } from '../../models/contactInfo';
import useProjects from '../../hooks/useProjects';
import { Project } from '../../models/project';
import useUsers from '../../hooks/useUsers';
import { UserDto } from '../../models/user';
import { canArchiveInvoice } from '../../utils/archiveRules';
import openPdf, { previewPdf } from '../../utils/openPdf';

const validate = (values: Invoice): ValidationErrors => {
  const errors: ValidationErrors = {};
  // item 465 — the Service Address name is required (inherited from the WO/proposal on generate)
  if (!values.serviceName) {
    errors.serviceName = 'Required';
  }
  // number is optional — left blank, the backend auto-generates it (item 101)
  if (!values.projectId) {
    errors.projectId = 'Required';
  }
  if (!values.date) {
    errors.date = 'Required';
  }
  return errors;
};

const InvoiceForm: React.FC = () => {
  const { id } = useParams<{id: string}>();
  const history = useHistory();
  const toast = useToast();
  const { isOpen: finalizeOpen, onOpen: openFinalize, onClose: closeFinalize } = useDisclosure();
  // item 434 — "Finalize & send": after a successful finalize, pop the send dialog.
  // sendAfterRef marks intent; sendSignal bumps to auto-open SendDocumentButton.
  const sendAfterRef = React.useRef(false);
  const [sendSignal, setSendSignal] = React.useState(0);
  // item 317 — pre-finalize commission split adjuster
  const { isOpen: adjustOpen, onOpen: openAdjust, onClose: closeAdjust } = useDisclosure();
  const {
    tryUpdate, tryArchive, tryVoid, data: invoice, status,
  } = useInvoice({ id });
  // item 142: archive/void = Pulse Group admin only. item 300: once an invoice is PAID,
  // only an admin/super-admin (canModerate) may change it — accountants are locked out.
  const { isSuperAdmin, canModerate, isAdmin } = useCurrentUser();
  const { data: proposals, status: proposalStatus } = useProposals();
  const { data: workOrders, status: workOrderStatus } = useWorkOrders();
  const { data: contacts, status: contactStatus } = useContacts();
  const { data: projects, status: projectsStatus } = useProjects();
  const { data: users, status: usersStatus } = useUsers();
  // item 1488 (Chad) — SERVICE invoice = its work order has no proposal; PROJECT = proposal-born.
  // Drives the labels + generation defaults (service: itemized, reason + work performed on).
  const isService = !!invoice && !invoice.proposalId;

  const infoSection = (
    <>
      {/* dart (2026-07-14) — #24: prompt to enroll the monthly subscription for an RNWY deal
          (self-hides for non-RNWY invoices or once a subscription exists for the project).
          dart (2026-07-15, Chad) — only once this is a CLOSED FINAL one-time invoice: finalized and
          not a partial "- Deposit" (a 100% single invoice or a "- Final"). Admin-gated inside. */}
      <RnwyEnrollPanel
        proposalId={invoice?.proposalId}
        enabled={!!invoice?.isFinalized && !(invoice?.serviceName ?? '').toLowerCase().includes('- deposit')}
      />
      {/* item 465 — Service Address (end-user) FIRST; invoices carry their own serviceName
          (inherited from the source WO/proposal at generation, editable here) */}
      <ServiceAddressSection nameField="serviceName" />
      <Divider my={4} />
      <FormRow
        heading="Basic Information"
        subHeading="Information that will be the main identifiers for the invoice"
      >
        <ResponsiveStack>
          <TextInputControl
            name="invoiceNumber"
            label="Invoice Number"
            isRequired={false}
            placeholder="Uses your standardized numbering — fill in to override with a custom number"
          />
          <SelectInputControl name="customerId" label="Customer" isRequired>
            {/* item 434 — a select with a value missing from its options renders BLANK;
                if the stored customer isn't in the loaded list (filtering/races), keep
                an explicit option for it so the customer always shows */}
            {contactStatus === 'success' && invoice?.customerId
              && !contacts?.index?.some((c: ContactDto) => c.id === invoice.customerId) && (
              <option value={invoice.customerId}>{invoice.customerName || 'Current customer'}</option>
            )}
            {contactStatus === 'success' && contacts && contacts.index.map((contact: ContactDto) => <option key={contact.id} value={contact.id}>{contact.name}</option>)}
          </SelectInputControl>
        </ResponsiveStack>
        <ResponsiveStack>
          <SelectInputControl name="projectId" label="Project" isRequired>
            {projectsStatus === 'success' && projects && projects.index.map((project: Project) => <option key={project.id} value={project.id}>{project.name}</option>)}
          </SelectInputControl>
          <DateInputControl
            name="date"
            label="Date"
            isRequired
          />
          {/* item 597 — platform is a READ-ONLY pass-through carried from the source work
              order/proposal (no invoice column); edit it on the proposal or work order */}
          <FormControl>
            <FieldLabel htmlFor="invoice-platform">Platform</FieldLabel>
            <Input
              id="invoice-platform"
              value={invoice?.effectivePlatform || '—'}
              isReadOnly
              variant="filled"
              tabIndex={-1}
            />
            <FormHelperText>Carried from the work order / proposal.</FormHelperText>
          </FormControl>
          {/* item 609 — the source work order's number IS the contractual "job code"
              (read-only pass-through; also the Sales Bible column) */}
          <FormControl>
            <FieldLabel htmlFor="invoice-job-code">Job Code (Work Order #)</FieldLabel>
            <Input
              id="invoice-job-code"
              value={invoice?.workOrderNumber || '—'}
              isReadOnly
              variant="filled"
              tabIndex={-1}
            />
            <FormHelperText>The work order number — the contract / job code.</FormHelperText>
          </FormControl>
        </ResponsiveStack>
        <ResponsiveStack>
          <SelectInputControl name="proposalId" label="Proposal">
            {proposalStatus === 'success' && proposals && proposals.index.map((proposal: Proposal) => <option key={proposal.proposalNumber} value={proposal.id}>{proposal.proposalTitle}</option>)}
          </SelectInputControl>
          <SelectInputControl name="workOrderId" label="Work Order">
            {workOrderStatus === 'success' && workOrders && workOrders.index.map((workOrder: WorkOrder) => <option key={workOrder.id} value={workOrder.id}>{workOrder.workOrderTitle}</option>)}
          </SelectInputControl>
        </ResponsiveStack>
        {/* item 179 — the state whose sales-tax rate applies to the taxable lines */}
        <ResponsiveStack>
          <SelectInputControl
            name="taxState"
            label="Tax State"
            isRequired={false}
            helperText="Sets the sales-tax rate applied to taxable lines on this document."
          >
            <option value="">— No tax —</option>
            {US_STATES.map((s) => (
              <option key={s.code} value={s.code}>{`${s.code} — ${s.name}`}</option>
            ))}
          </SelectInputControl>
        </ResponsiveStack>
        <ResponsiveStack>
          {/* item 733 (Chad) — the CLIENT's PO number; REQUIRED to finalize + printed on the invoice */}
          <TextInputControl
            name="customerPoNumber"
            label="Client PO # (required to finalize)"
          />
          <TextInputControl
            name="pointOfContact"
            label="Point Of Contact"
          />
          <SelectInputControl name="salesRepId" label="Sales Rep">
            {usersStatus === 'success' && users
            // eslint-disable-next-line max-len
            && users.index.map((user: UserDto) => <option key={user.id} value={user.id}>{user.name.toUpperCase()}</option>)}
          </SelectInputControl>
        </ResponsiveStack>
        {/* item 1310 (Chad) — shipment tracking (from the SO); prints on the PDF + emails as a link.
            item 1331 (Chad) — its OWN full-width row (was squeezed into the 3-up row above): switch + URL inline. */}
        <ResponsiveStack>
          <TrackingUrlField docLabel="invoice" />
        </ResponsiveStack>
        <ResponsiveStack>
          {/* item 434 — SIGNED adjustment: deposit splits store a negative; the old
              no-negatives input ate the sign on save and INFLATED the total */}
          <CurrencyInputControl
            name="discount"
            // item 1487 — always REDUCES the total (saved negative whatever sign is typed)
            label="Discount (reduces the total)"
            allowNegative
          />
          <TextInputControl
            name="discountReason"
            label="Discount Reason"
          />
        </ResponsiveStack>
      </FormRow>

      {/* item 543 — client-facing narrative that prints on the invoice PDF: the original
          request (carried from the work order) + what the technician performed. Editable here. */}
      <Divider my={4} />
      <FormRow
        heading={isService ? 'Reason for Work' : 'Proposal Scope'}
        subHeading={isService
          ? "The client's original request — prints when “Include reason for work” is on"
          : 'The statement of work from the proposal — prints when “Include proposal scope” is on'}
      >
        <Field<string>
          name="reasonForWork"
          component={MarkdownTextInput}
          label={isService ? 'Reason for Work' : 'Proposal Scope'}
        />
      </FormRow>
      <Divider my={4} />
      <FormRow
        heading="Work Performed"
        subHeading="The service notes — what was done on the job; prints when “Include work performed” is on"
      >
        <Field<string>
          name="workPerformed"
          component={MarkdownTextInput}
          label="Work Performed"
        />
      </FormRow>
      <Divider my={4} />
      <FormRow
        heading="Internal Notes"
        subHeading="Internal only — never shown to the client / never on the PDF"
      >
        <Field<string>
          name="internalNotes"
          component={MarkdownTextInput}
          label="Internal Notes"
        />
      </FormRow>

      <Divider my={4} />
      {/* item 1488 (Chad) — opt-in output toggles; defaults set at generation by the invoice kind.
          Once finalized these stay editable in the PDF OUTPUT panel above the locked body. */}
      <FormRow
        heading="PDF Output"
        subHeading={isService
          ? 'SERVICE invoice — itemized, reason for work + work performed on by default'
          : 'PROJECT invoice — proposal scope off by default, work performed on'}
      >
        <Field<boolean>
          name="itemize"
          component={ToggleInput}
          label="Itemize lines (group price)"
          helperText="On: each line shows its price and amount. Off: lines still list with SKU and quantity, but only the invoice total is priced."
        />
        {/* item 1336 → 1488 (Chad) — the toggle adds WHO only; a line's date always prints */}
        <Field<boolean>
          name="showPerformedOnPdf"
          component={ToggleInput}
          label="Show who performed"
          helperText="Adds who performed each line beside its date. Line dates always print."
        />
        <Field<boolean>
          name="includeScopeOnPdf"
          component={ToggleInput}
          label={isService ? 'Include reason for work' : 'Include proposal scope'}
          helperText={isService ? "Prints the client's request on the PDF." : "Prints the proposal's statement of work on the PDF."}
        />
        <Field<boolean>
          name="includeWorkPerformedOnPdf"
          component={ToggleInput}
          label="Include work performed"
          helperText="Prints the service notes. Nothing prints when the field is blank."
        />
      </FormRow>
      {/* item 792 (Chad) — the client's PO rides the whole chain: uploads from the proposal /
          work order surface here (backend chain-resolves), and Send attaches them with the PDF. */}
      {id !== NIL && <ReceivedPoAttachments docId={id} />}
      {/* item 1574 — signed delivery doc (uploaded here or on the WO/SO/PO); Send attaches it */}
      {id !== NIL && <ReceivedPoAttachments docId={id} kind="delivery" />}
    </>
  );

  // item: a finalized (locked) or voided invoice can't be edited/saved — the footer
  // collapses to Send / Cancel / View PDF / View Account PDF, plus Archive / Void for a
  // Pulse Group admin (item 142). Archived/voided invoices then list under their bucket.
  // item 441 — isLocked never actually arrived on the web DTO (isFinalized does now);
  // derive locked-ness from the states that mean it so finalized/void truly hide Save
  const isLocked = !!invoice?.isLocked || !!invoice?.isFinalized || !!invoice?.isVoid;
  // item 300 — a PAID invoice is admin-only to change; an accountant (not canModerate)
  // gets a read-only form once it's Paid/PaidEntered. (Server-side SecurityBL is item 205.)
  const isPaid = invoice?.status === InvoiceStatus.Paid || invoice?.status === InvoiceStatus.PaidEntered;
  const paidLockedForMe = isPaid && !canModerate;
  const isVoid = !!invoice?.isVoid;
  const isArchive = !!invoice?.isArchive;

  // item 441 — the terminal-state ribbon: VOID beats PAID beats FINALIZED
  let statusStamp: React.ReactNode;
  if (invoice && id !== NIL) {
    if (isVoid) {
      statusStamp = <DocumentStatusStamp tone="negative" label="VOID" detail="This invoice is void — regenerate from the work order if a corrected one is needed." />;
    } else if (isPaid) {
      statusStamp = <DocumentStatusStamp tone="positive" label="PAID" detail="Payment received — the invoice is locked and its contents are final." />;
    } else if (invoice.isFinalized) {
      statusStamp = <DocumentStatusStamp tone="neutral" label="FINALIZED" detail="View-only — contents are locked. Void it to correct and regenerate." />;
    }
    // item 1488 (Chad) — PDF output stays editable after finalize (not on void/archive)
    if (invoice.isFinalized && !isVoid && !isArchive) {
      statusStamp = (
        <>
          {statusStamp}
          <InvoicePdfOutputPanel invoice={invoice} />
        </>
      );
    }
    // item 1163e — a finalized-unpaid invoice also states its collections-dunning trail
    // (last auto-reminder, muted/opted-out, when the ladder next fires). Admin-fed; renders
    // nothing for everyone else.
    if (invoice.isFinalized && !isVoid && !isPaid) {
      statusStamp = (
        <>
          {statusStamp}
          <InvoiceReminderChip invoiceId={id} customerId={invoice.customerId} dateStr={invoice.date} />
        </>
      );
    }
  }

  const doArchive = async () => {
    try {
      await tryArchive(id);
      toast({ status: 'success', title: 'Invoice archived' });
      history.goBack();
    } catch (e: any) {
      toast({ status: 'error', title: 'Archive failed', description: e?.response?.status === 403 ? 'Only a Pulse Group admin can archive.' : 'Try again.' });
    }
  };
  // EPIC_VOID_FLOW_2026-10 — the shared dialog asks for the reason + Notify customer, and the
  // hook toasts the outcome (or the server's refusal); leave only once it actually voided
  const doVoid = async () => {
    if (await tryVoid(id)) history.goBack();
  };

  return (
    <>
      <DocumentFormScaffold<Invoice, InvoiceItem>
        config={{
          title: 'INVOICE',
          saveLabel: 'Save Invoice',
          icon: IconInvoice,
          iconWhite: IconInvoiceWhite,
          doc: invoice,
          status,
          tryUpdate,
          validate,
          getItems: (doc) => doc.invoiceItems,
          getCategoryNames: (doc) => doc.categoryNames,
          applyScaffoldState: (values, state) => {
            values.categoryNames = state.categoryNames;
            values.invoiceItems = state.items;
            values.id = state.id;
          },
          ItemsElement: InvoiceItemsFormElement,
          infoSection,
          // a locked/finalized invoice can't be saved (Save hidden)
          hideSave: isLocked || paidLockedForMe,
          // item 874 (Chad) — and it can't be EDITED either: hiding Save still left every
          // field editable with zero indication. The scaffold lock makes the whole body inert
          // + dimmed, with an unmissable banner saying what to do instead.
          readOnlyLock: isLocked,
          readOnlyNotice: invoice?.isVoid
            ? 'This invoice is VOID — it is a permanent record and cannot be edited.'
            : 'This invoice is FINALIZED — its content is permanently locked. Need to change it? VOID this invoice below, then generate a corrected one from the work order (voiding the last live invoice reopens it).',
          statusStamp,
          // item 577 — PRIMARY cluster: the invoice's status verbs (Finalize while a
          // draft; Archive/Void once locked, Pulse super-admin only, item 142).
          footerPrimary: id !== NIL ? (
            <>
              {/* FINALIZE: locks the invoice permanently; only shown while it's still a
                  draft. item 434 — split: plain Finalize, or Finalize & send (same lock,
                  then the send dialog opens). */}
              {invoice && !invoice.isFinalized && !invoice.isVoid && (
              <>
                <Button
                  size="sm"
                  leftIcon={<FaLock />}
                  variant="outline"
                  onClick={() => { sendAfterRef.current = false; openFinalize(); }}
                >
                  Finalize
                </Button>
                <Button
                  size="sm"
                  leftIcon={<FaLock />}
                  variant="outline"
                  onClick={() => { sendAfterRef.current = true; openFinalize(); }}
                >
                  Finalize &amp; send
                </Button>
              </>
              )}
              {isLocked && isSuperAdmin && !isArchive && (
                <>
                  {/* item 1570 — archive only once it's done (void / paid / written off) */}
                  {canArchiveInvoice({ isVoid, status: invoice?.status }) && (
                    <Button size="sm" variant="outline" colorScheme="orange" leftIcon={<FaFolderOpen />} onClick={doArchive}>
                      Archive
                    </Button>
                  )}
                  {!isVoid && (
                    <Button size="sm" variant="danger" leftIcon={<FaBan />} onClick={doVoid}>
                      Void
                    </Button>
                  )}
                </>
              )}
            </>
          ) : undefined,
          // item 577 — UTILITY cluster: commissions + PDFs + send (items 23/73/317/301)
          footerActions: id !== NIL ? (
            <>
              {/* item 317 — adjust the commission split before finalizing (admin only, draft only) */}
              {invoice && !invoice.isVoid && isAdmin && (
              <Button size="sm" variant="ghost" onClick={openAdjust}>
                {invoice.isFinalized ? 'View commissions' : 'Adjust commissions'}
              </Button>
              )}
              {/* item 796b (Chad) — Preview (inline) + Download, like the other doc forms */}
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<FiEye />}
                onClick={() => {
                  previewPdf(`invoice/${id}/pdf`).catch(() => toast({
                    status: 'error', title: 'Could not open the PDF', duration: 6000, isClosable: true,
                  }));
                }}
              >
                Preview PDF
              </Button>
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<FaFilePdf />}
                onClick={() => {
                  openPdf(`invoice/${id}/pdf`).catch(() => toast({
                    status: 'error', title: 'Could not open the PDF', duration: 6000, isClosable: true,
                  }));
                }}
              >
                Download PDF
              </Button>
              {/* #27 — output setting: the same invoice with the (often long) scope left off */}
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<FaFilePdf />}
                onClick={() => {
                  openPdf(`invoice/${id}/pdf?includeScope=false`).catch(() => toast({
                    status: 'error', title: 'Could not open the PDF', duration: 6000, isClosable: true,
                  }));
                }}
              >
                PDF (no scope)
              </Button>
              {/* item 301: Accounting PDF + Send appear ONLY after the invoice is finalized.
                  A draft shows just Finalize & Send + View PDF (you can't send a draft). */}
              {invoice?.isFinalized && (
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<FaFilePdf />}
                onClick={() => {
                  openPdf(`invoice/${id}/pdf?accounting=true`).catch(() => toast({
                    status: 'error', title: 'Could not open the PDF', duration: 6000, isClosable: true,
                  }));
                }}
              >
                Accounting PDF
              </Button>
              )}
              {/* item 111/32: email the invoice to the client (+ optional Stripe pay link) —
                  item 301: only once finalized (no sending a draft) */}
              {invoice?.isFinalized && (
                <SendDocumentButton
                  documentType="Invoice"
                  documentId={id}
                  documentLabel={invoice?.invoiceNumber}
                  autoOpenSignal={sendSignal}
                  // item 576 — subject/body pre-fill from the server's ONE status-aware copy
                  // rule (milestone + finalized state), same as the grid's Send to client
                />
              )}
            </>
          ) : undefined,
        }}
      />
      {/* item 109: finalize modal — also reachable from the invoice grid ⋮ menu */}
      <FinalizeInvoiceModal
        isOpen={finalizeOpen}
        onClose={closeFinalize}
        invoiceId={id}
        showSend
        onFinalized={(send) => {
          // item 434 (form's Finalize & send button) OR item 462c (chosen in-modal)
          if (send || sendAfterRef.current) {
            sendAfterRef.current = false;
            setSendSignal((n) => n + 1); // auto-open the send dialog
          }
        }}
      />
      {/* item 317: per-invoice commission split adjuster */}
      <CommissionAdjustModal isOpen={adjustOpen} onClose={closeAdjust} invoiceId={id} locked={!!invoice?.isFinalized} />
    </>
  );
};

export default withAuthenticationRequired(InvoiceForm);
